import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type { Location, LocationCreate, LocationUpdate } from '@/types/api';

/**
 * Mutations-Hooks für Standorte (Spec S8-06, Kontrakt 1).
 *
 * Endpunkte (Form aus docs/api/openapi.yaml, operationIds
 * createCustomerLocation / updateLocation / deleteLocation):
 *   POST   /customers/{customer_id}/locations
 *   PATCH  /locations/{location_id}
 *   DELETE /locations/{location_id}   (soft)
 *
 * Der Lese-Hook bleibt useCustomerLocations (hooks/useCustomers.ts).
 * Invalidation: der komplette customers-Baum — deckt die Standort-Liste
 * (customers.detail.{id}.locations) UND die Stats (locations_count) ab,
 * beide liegen unter dem 'customers'-Präfix. Delete invalidiert zusätzlich
 * den systems-Baum, weil das Backend location_id betroffener Systeme nullt.
 */

function invalidateCustomers(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: queryKeys.customers.all });
}

export interface CreateLocationInput {
  customerId: string;
  body: LocationCreate;
}

export function useCreateLocation(): ReturnType<
  typeof useMutation<Location, Error, CreateLocationInput>
> {
  const qc = useQueryClient();
  return useMutation<Location, Error, CreateLocationInput>({
    mutationFn: ({ customerId, body }) =>
      api.post<Location, LocationCreate>(`/customers/${customerId}/locations`, body),
    onSuccess: () => invalidateCustomers(qc),
  });
}

export interface UpdateLocationInput {
  locationId: string;
  body: LocationUpdate;
}

export function useUpdateLocation(): ReturnType<
  typeof useMutation<Location, Error, UpdateLocationInput>
> {
  const qc = useQueryClient();
  return useMutation<Location, Error, UpdateLocationInput>({
    mutationFn: ({ locationId, body }) =>
      api.patch<Location, LocationUpdate>(`/locations/${locationId}`, body),
    onSuccess: () => invalidateCustomers(qc),
  });
}

export interface DeleteLocationInput {
  locationId: string;
}

export function useDeleteLocation(): ReturnType<
  typeof useMutation<void, Error, DeleteLocationInput>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteLocationInput>({
    mutationFn: ({ locationId }) => api.delete(`/locations/${locationId}`),
    onSuccess: () => {
      invalidateCustomers(qc);
      // Backend setzt location_id betroffener Systeme auf NULL (Soft-Delete
      // löst nur die Zuordnung) -> Systemlisten/-details neu laden.
      void qc.invalidateQueries({ queryKey: queryKeys.systems.all });
    },
  });
}
